import { Button, Portal, Text } from '@chakra-ui/react';
import { useDonateButtonRef } from '@contexts/DonateButtonRef';
import { FormValues, ManualPM } from '@types';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import * as Sentry from '@sentry/react';
import composeErrorMessage from '@utils/composeErrorMessage';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';

interface Props {
  method: ManualPM;
  formIsLoading: boolean;
  onComplete: () => Promise<void>;
  onError: (error: string) => void;
}

const ManualPanel: FC<Props> = ({ method, formIsLoading, onComplete, onError }) => {
  const { ref } = useDonateButtonRef();
  const { watch } = useFormContext<FormValues>();
  const { checkout } = useCheckoutConfigContext();

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleDonate = async () => {
    setLoading(true);
    try {
      await onComplete();
    } catch (err) {
      console.error(err);
      onError(t("We couldn't process your payment, please try again later"));
      Sentry.captureMessage(
        composeErrorMessage({
          code: 'MANUAL_ERROR',
          errorObject: err,
          checkout,
          campaignId: checkout.campaign.id,
          organization: checkout.organization.name,
          token: checkout.token,
        }),
      );
    }

    setLoading(false);
  };

  if (!ref?.current) return null;

  return (
    <div>
      <Text>{method.manual_description}</Text>

      <Portal containerRef={ref}>
        <When condition={watch('paymentMethodId') === method.id}>
          <Button
            size="lg"
            w={{ base: 'full', lg: '100px' }}
            alignSelf="flex-end"
            mt="auto"
            colorScheme="primary"
            isLoading={loading || formIsLoading}
            disabled={loading || formIsLoading}
            onClick={handleDonate}
          >
            {t('Donate')}
          </Button>
        </When>
      </Portal>
    </div>
  );
};

export default ManualPanel;
